// contactsApi — per-conversation contact records for the SMS Text Inbox:
// a name, tags, notes and a home address attached to a phone number, plus a
// shared catalog of assignable tags.
//
// Kept SEPARATE from the `customers` collection on purpose: most texters have
// no order, so folding them into `customers` would pollute the order-create
// autocomplete. The doc id is the E.164 phone (same convention as `customers`,
// so a future merge is trivial), but the client lookup is keyed by the 10-digit
// bare form (`normalizePhone`) because inbound forwarded texts store the sender
// number un-normalized while outbound rows store E.164 — both must resolve to
// one contact. Uses firebase/compat, consistent with services/customers.ts.

import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

import { db } from './firebase';
import { toE164, normalizePhone } from './phone';
import { USE_SUPABASE, supabase, subscribeTable, msFromISO0, fetchAllRows } from './supabaseClient';

/** One saved delivery address on a contact (UPE-69). A customer can keep
 *  several — Home, Work, a friend's — and exactly one is the default. `id` is a
 *  stable key so an edit/remove targets the right row across re-renders. */
export interface SmsAddress {
  id: string;         // stable local id (never shown)
  label: string;      // 'Home', 'Work', … (may be blank)
  address: string;    // free text — same shape as the old single address
  isDefault: boolean; // exactly one entry is true
  // Google verification — SAME contract as the order model (services/
  // addressValidation.ts), so an address saved here is already validated by the
  // time it flows into an order. Absent = never checked.
  verified?: boolean;        // Google confirmed the address
  kept?: boolean;            // dispatcher kept their text over a "did you mean"
  lat?: number;
  lng?: number;
  placeId?: string;
  formattedAddress?: string; // Google's canonical form
}

export interface SmsContact {
  phone: string;     // E.164, mirrors the doc id (read-only)
  name: string;      // A1
  tags: string[];    // A2 — e.g. ['E-Transfer Only', 'Blocked']
  notes: string;     // A4
  address: string;   // A5 — the DEFAULT address as free text. Kept in sync with
                     // `addresses` (below) so anything that still reads the old
                     // single field gets the default; separate from customers.address.
  addresses: SmsAddress[]; // UPE-69 — all saved addresses, default first-class
  updatedAt: number; // ms epoch (0 when unknown / write not yet confirmed)
  updatedBy: string;
}

// Seeded the first time a tag is created; also the fallback the UI shows before
// the catalog doc loads. 'Blocked' is reserved — it will later mute a number's
// inbound-text notifications (feature H2). Keep it in this list.
export const DEFAULT_TAGS = ['E-Transfer Only', 'Cash Only', 'Local', 'Blocked'];
export const BLOCKED_TAG = 'Blocked';
/** Set by the SMS queue itself (netlify/functions/_sms-deliverability.js) when
 *  the network rejects a text to this number on both attempts. A WARNING, not a
 *  block (Umer, 2026-09-10): the composer still sends, each text gets two
 *  attempts, and the queue removes the tag the moment one goes through. An
 *  admin can also remove it in the contact panel like any other tag. */
export const NOT_DELIVERED_TAG = 'Not delivered';

const CONTACTS_COL = 'smsContacts';
const TAGS_COL = 'smsTags';
const TAGS_DOC = 'catalog';

function tsToMs(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    try {
      return (value as { toMillis: () => number }).toMillis();
    } catch {
      return 0;
    }
  }
  return 0;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
/** Compare key for de-duplication: case- and whitespace-insensitive. */
const addrKey = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Canonical address list: drops blank rows (no label AND no address), assigns a
 * stable id to any row missing one, DROPS a repeat of an address already in the
 * list (no identical duplicates), preserves Google-verification fields, and
 * guarantees EXACTLY one default — the first row flagged default wins; if none
 * is flagged, the first row is. Pure and idempotent (re-normalizing its own
 * output is a no-op), so it's the one place both the reader and every writer
 * pass through. Exported for tests.
 */
export function normalizeAddressList(raw: unknown): SmsAddress[] {
  const rows = Array.isArray(raw) ? raw : [];
  const out: SmsAddress[] = [];
  const seenAddr = new Set<string>();
  rows.forEach((r, i) => {
    const rec = (r && typeof r === 'object') ? (r as Record<string, unknown>) : {};
    const label = str(rec.label).trim();
    const address = str(rec.address).trim();
    if (!label && !address) return; // a fully empty row is not an address
    // Never store the same address twice — keep the first, drop later repeats.
    if (address) {
      const key = addrKey(address);
      if (seenAddr.has(key)) return;
      seenAddr.add(key);
    }
    const a: SmsAddress = { id: str(rec.id) || `a${i}`, label, address, isDefault: rec.isDefault === true };
    // Carry only present fields — Firestore rejects `undefined`.
    if (rec.verified === true) a.verified = true;
    if (rec.kept === true) a.kept = true;
    const lat = num(rec.lat); if (lat !== undefined) a.lat = lat;
    const lng = num(rec.lng); if (lng !== undefined) a.lng = lng;
    const placeId = str(rec.placeId); if (placeId) a.placeId = placeId;
    const formatted = str(rec.formattedAddress); if (formatted) a.formattedAddress = formatted;
    out.push(a);
  });
  if (out.length) {
    let seen = false;
    for (const a of out) {
      if (a.isDefault && !seen) { seen = true; } else { a.isDefault = false; }
    }
    if (!seen) out[0].isDefault = true;
  }
  return out;
}

/** The default row's address (the free-text value), or '' when there is none. */
export function defaultAddress(addresses: SmsAddress[]): string {
  return addresses.find((a) => a.isDefault)?.address ?? '';
}

/** Read the saved address list, migrating a legacy single `address` string into
 *  one default row so customers who already have an address keep it. */
function readAddresses(data: Record<string, unknown>): SmsAddress[] {
  if (Array.isArray(data.addresses)) return normalizeAddressList(data.addresses);
  const legacy = str(data.address).trim();
  return legacy ? [{ id: 'primary', label: 'Home', address: legacy, isDefault: true }] : [];
}

/** Shape a raw Firestore contact doc into an SmsContact. Exported for tests. */
export function normalizeContact(id: string, data: Record<string, unknown>): SmsContact {
  const addresses = readAddresses(data);
  return {
    phone: (data.phone as string) ?? id,
    name: (data.name as string) ?? '',
    tags: Array.isArray(data.tags)
      ? (data.tags as unknown[]).filter((t): t is string => typeof t === 'string')
      : [],
    notes: (data.notes as string) ?? '',
    // Legacy single field mirrors the default, so old readers keep working.
    address: defaultAddress(addresses) || str(data.address),
    addresses,
    updatedAt: tsToMs(data.updatedAt),
    updatedBy: (data.updatedBy as string) ?? '',
  };
}

/** Build the digit-keyed lookup map (normalizePhone) from contact records, so a
 *  raw inbound `from` and an E.164 outbound `to` for the same person resolve to
 *  one contact. Exported so the join logic is unit-testable without Firestore. */
export function buildContactMap(records: SmsContact[]): Map<string, SmsContact> {
  const byDigits = new Map<string, SmsContact>();
  for (const rec of records) {
    const key = normalizePhone(rec.phone);
    if (key) byDigits.set(key, rec);
  }
  return byDigits;
}

/** Look up a contact for any phone-number format (tolerant match). */
export function contactFor(
  byDigits: Map<string, SmsContact>,
  number: string,
): SmsContact | undefined {
  return byDigits.get(normalizePhone(number));
}

/** True when the number has a contact carrying the reserved 'Blocked' tag.
 *  Tolerant to number format. (For feature H2 — notification suppression.) */
export function isBlocked(byDigits: Map<string, SmsContact>, number: string): boolean {
  const c = byDigits.get(normalizePhone(number));
  return !!c && c.tags.includes(BLOCKED_TAG);
}

/** True when the queue's last two attempts to this number were rejected. */
export function isNotDeliveredContact(c: SmsContact | undefined): boolean {
  return !!c && c.tags.includes(NOT_DELIVERED_TAG);
}

const noop = () => {};

// ── Supabase mode ───────────────────────────────────────────────────────────
// Postgres twins of the Firestore collections (migration 0009): sms_contacts
// (one row per E.164 phone) and sms_tags (the shared catalog, one row per
// tag). RLS restricts both to the dispatch tier; realtime keeps the maps live
// through the same subscribeTable contract the rest of the app uses.
const sbReadAddresses = (r: any): SmsAddress[] =>
  readAddresses(Array.isArray(r.addresses) && r.addresses.length ? r : { ...r, addresses: undefined });

const sbContactFromRow = (r: any): SmsContact => ({
  phone: r.phone,
  name: r.name ?? '',
  tags: Array.isArray(r.tags) ? r.tags.filter((t: unknown): t is string => typeof t === 'string') : [],
  notes: r.notes ?? '',
  // UPE-69: `addresses` jsonb (migration 0028) is the list; the legacy single
  // `address` column mirrors the default, exactly like the Firestore doc.
  // The column defaults to '[]', which is NOT "no list" — a Firestore doc
  // without the field falls back to the legacy address, so an empty array
  // must do the same here or every pre-0028 contact loses its address in
  // the order form's suggestions (staging: "test-2" had an address, no list).
  address: defaultAddress(sbReadAddresses(r)) || (r.address ?? ''),
  addresses: sbReadAddresses(r),
  updatedAt: msFromISO0(r.updated_at),
  updatedBy: r.updated_by ?? '',
});

const sbStamp = (user: { id: string }) => ({
  updated_at: new Date().toISOString(),
  updated_by: user.id,
});

/** Read-modify-write of one contact's tag array (Postgres has no arrayUnion
 *  through PostgREST; last-writer-wins is fine at dispatch-desk concurrency). */
async function sbSetTags(phone: string, mutate: (tags: string[]) => string[], user: { id: string }): Promise<void> {
  const sb = supabase();
  const { data, error } = await sb.from('sms_contacts').select('tags').eq('phone', phone).maybeSingle();
  if (error) throw new Error(`sms_contacts read: ${error.message}`);
  const tags = mutate(Array.isArray(data?.tags) ? data!.tags : []);
  const { error: writeErr } = await sb.from('sms_contacts').upsert({ phone, tags, ...sbStamp(user) });
  if (writeErr) throw new Error(`sms_contacts write: ${writeErr.message}`);
}

/** Live-subscribe to every contact. Cache-first (Firestore IndexedDB
 *  persistence is on), so the callback paints from cache before the network.
 *  Returns an unsubscribe fn. */
/** Columns a CONTACT LIST needs: the name to label a thread, the tags to mute
 *  a blocked number. Deliberately not notes/address/addresses — at ~20,000
 *  contacts (Umer, 2026-09-09) those turn an unfiltered read into megabytes
 *  paged 1,000 rows at a time, on a screen that only ever shows the name.
 *  Callers that genuinely need the whole record (the order form's saved
 *  addresses) pass their own columns; the open contact panel uses fetchContact. */
export const CONTACT_LIST_COLUMNS = 'phone,name,tags';

export function subscribeContacts(
  cb: (byDigits: Map<string, SmsContact>) => void,
  onError?: (e: Error) => void,
  opts?: { columns?: string; tagged?: string },
): () => void {
  if (USE_SUPABASE) {
    return subscribeTable<SmsContact>('sms_contacts', sbContactFromRow,
      (rows) => cb(buildContactMap(rows)), {
        pk: 'phone',
        select: opts?.columns,
        // `tagged`: only contacts carrying this tag — a server-side `cs`
        // filter, so the blocked-number set is one small request rather than
        // the whole table.
        ...(opts?.tagged ? { contains: { column: 'tags', value: opts.tagged } } : {}),
        onError,
      });
  }
  if (!db) {
    cb(new Map());
    return noop;
  }
  return db.collection(CONTACTS_COL).onSnapshot(
    (snap) => {
      const records = snap.docs.map((d) =>
        normalizeContact(d.id, d.data() as Record<string, unknown>),
      );
      cb(buildContactMap(records));
    },
    (err) => {
      onError?.(err as Error);
    },
  );
}

/** Columns the New Order form's suggestions read off a contact (orderCustomerSuggest). */
export const ORDER_FORM_CONTACT_COLUMNS = 'phone,name,address,addresses';

/** The contacts that carry a saved address, for the New Order form (Supabase mode).
 *  Phase 1 (2026-09-30): the form live-subscribed to the WHOLE table each time it
 *  opened — ~20,000 rows, every column, 1,000 per request, plus a whole-table channel
 *  while it stayed open — to keep the few with an address. One filtered read of the
 *  four columns it uses; `address` still counts for a contact from before 0028's list. */
export async function fetchContactsWithAddresses(): Promise<SmsContact[]> {
  const rows = await fetchAllRows<any>(
    () => supabase().from('sms_contacts').select(ORDER_FORM_CONTACT_COLUMNS)
      .or('addresses.neq.[],address.neq.')
      .order('phone', { ascending: true }),
    undefined,
    { column: 'phone' },
  );
  // Keyset pages repeat their boundary row; fetchAllRows drops repeats by `id`, and a contact's key is its phone.
  const byPhone = new Map<string, any>();
  for (const r of rows) byPhone.set(r.phone, r);
  return [...byPhone.values()].map(sbContactFromRow).filter((c) => c.addresses.length > 0);
}

/** The COMPLETE record for one contact — notes and saved addresses included.
 *  The list subscription above carries only name and tags, so the contact panel
 *  reads the row it is about when it opens. Resolves null when there is no
 *  contact for that number yet (a brand-new conversation). */
export async function fetchContact(phone: string): Promise<SmsContact | null> {
  const key = normalizePhone(phone);
  if (!key) return null;
  if (USE_SUPABASE) {
    const { data, error } = await supabase()
      .from('sms_contacts').select('*').eq('phone', toE164(phone)).maybeSingle();
    if (error) throw new Error(error.message);
    return data ? sbContactFromRow(data) : null;
  }
  if (!db) return null;
  const snap = await db.collection(CONTACTS_COL).doc(toE164(phone)).get();
  return snap.exists ? normalizeContact(snap.id, snap.data() as Record<string, unknown>) : null;
}

/** Live-subscribe to the shared tag catalog. Falls back to DEFAULT_TAGS when the
 *  catalog doc is absent or empty. Returns an unsubscribe fn. */
export function subscribeTags(
  cb: (tags: string[]) => void,
  onError?: (e: Error) => void,
): () => void {
  if (USE_SUPABASE) {
    return subscribeTable<string>('sms_tags', (r: any) => r.tag as string,
      (tags) => cb(tags.length ? tags : DEFAULT_TAGS), { onError });
  }
  if (!db) {
    cb(DEFAULT_TAGS);
    return noop;
  }
  return db.collection(TAGS_COL).doc(TAGS_DOC).onSnapshot(
    (snap) => {
      const data = snap.data() as { tags?: unknown } | undefined;
      const tags = Array.isArray(data?.tags)
        ? (data!.tags as unknown[]).filter((t): t is string => typeof t === 'string')
        : [];
      cb(tags.length ? tags : DEFAULT_TAGS);
    },
    (err) => {
      onError?.(err as Error);
    },
  );
}

/** Live-subscribe to the set of BLOCKED numbers (as normalizePhone keys), for
 *  notification suppression (feature H2). Returns an unsubscribe fn. */
export function subscribeBlockedNumbers(
  cb: (blocked: Set<string>) => void,
  onError?: (e: Error) => void,
): () => void {
  // NotificationHandler mounts this on EVERY page. Seen on wolfdrivers.com
  // (2026-09-10): with no column list and no filter it walked ~5,000 contacts —
  // five sequential pages of select=*, notes and address jsonb included — to
  // find the few tagged Blocked, and it did so on every page load next to the
  // Text Inbox's own walk. Now: phone + tags, and only the tagged rows.
  // (Firestore mode still filters the full map; that backend has no `cs`.)
  return subscribeContacts((byDigits) => {
    const blocked = new Set<string>();
    for (const [key, c] of byDigits) {
      if (c.tags.includes(BLOCKED_TAG)) blocked.add(key);
    }
    cb(blocked);
  }, onError, { columns: 'phone,tags', tagged: BLOCKED_TAG });
}

function stamp(user: { id: string }): Record<string, unknown> {
  return {
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedBy: user.id,
  };
}

/** Create or merge a contact's editable fields (name / notes / address). Doc id
 *  = E.164 of the number. Only present fields are written (Firestore rejects
 *  `undefined`). Fire-and-forget from the auto-save UI. */
export async function upsertContact(
  number: string,
  patch: Partial<Pick<SmsContact, 'name' | 'notes' | 'address'>>,
  user: { id: string },
): Promise<void> {
  const id = toE164(number);
  if (!id) throw new Error('upsertContact: number required');
  if (USE_SUPABASE) {
    const payload: Record<string, unknown> = { phone: id, ...sbStamp(user) };
    if (typeof patch.name === 'string') payload.name = patch.name;
    if (typeof patch.notes === 'string') payload.notes = patch.notes;
    if (typeof patch.address === 'string') payload.address = patch.address;
    const { error } = await supabase().from('sms_contacts').upsert(payload);
    if (error) throw new Error(`sms_contacts write: ${error.message}`);
    return;
  }
  if (!db) throw new Error('Firebase not initialized');
  const payload: Record<string, unknown> = { phone: id, ...stamp(user) };
  if (typeof patch.name === 'string') payload.name = patch.name;
  if (typeof patch.notes === 'string') payload.notes = patch.notes;
  if (typeof patch.address === 'string') payload.address = patch.address;
  await db.collection(CONTACTS_COL).doc(id).set(payload, { merge: true });
}

/** Save a contact's full delivery-address list (UPE-69). Normalizes first (one
 *  default, no blank rows, ids assigned) and mirrors the default back into the
 *  legacy `address` field so old readers keep working. Doc id = E.164. */
export async function setContactAddresses(
  number: string,
  addresses: SmsAddress[],
  user: { id: string },
): Promise<void> {
  const id = toE164(number);
  if (!id) throw new Error('setContactAddresses: number required');
  const clean = normalizeAddressList(addresses);
  if (USE_SUPABASE) {
    const { error } = await supabase().from('sms_contacts').upsert(
      { phone: id, addresses: clean, address: defaultAddress(clean), ...sbStamp(user) },
    );
    if (error) throw new Error(`sms_contacts write: ${error.message}`);
    return;
  }
  if (!db) throw new Error('Firebase not initialized');
  await db.collection(CONTACTS_COL).doc(id).set(
    { phone: id, addresses: clean, address: defaultAddress(clean), ...stamp(user) },
    { merge: true },
  );
}

/** Assign a tag to a contact (creates the contact doc if it doesn't exist). */
export async function addTag(number: string, tag: string, user: { id: string }): Promise<void> {
  const id = toE164(number);
  if (!id || !tag) throw new Error('addTag: number and tag required');
  if (USE_SUPABASE) {
    return sbSetTags(id, (tags) => (tags.includes(tag) ? tags : [...tags, tag]), user);
  }
  if (!db) throw new Error('Firebase not initialized');
  await db.collection(CONTACTS_COL).doc(id).set(
    { phone: id, tags: firebase.firestore.FieldValue.arrayUnion(tag), ...stamp(user) },
    { merge: true },
  );
}

/** Remove a tag from a contact. */
export async function removeTag(number: string, tag: string, user: { id: string }): Promise<void> {
  const id = toE164(number);
  if (!id || !tag) throw new Error('removeTag: number and tag required');
  if (USE_SUPABASE) {
    return sbSetTags(id, (tags) => tags.filter((t) => t !== tag), user);
  }
  if (!db) throw new Error('Firebase not initialized');
  await db.collection(CONTACTS_COL).doc(id).set(
    { tags: firebase.firestore.FieldValue.arrayRemove(tag), ...stamp(user) },
    { merge: true },
  );
}

/** Add a new tag to the shared catalog so it becomes assignable to any number. */
export async function createTag(tag: string, user: { id: string }): Promise<void> {
  const clean = tag.trim();
  if (!clean) throw new Error('createTag: tag required');
  if (USE_SUPABASE) {
    const { error } = await supabase().from('sms_tags')
      .upsert({ tag: clean, created_by: user.id }, { onConflict: 'tag', ignoreDuplicates: true });
    if (error) throw new Error(`sms_tags write: ${error.message}`);
    return;
  }
  if (!db) throw new Error('Firebase not initialized');
  await db.collection(TAGS_COL).doc(TAGS_DOC).set(
    { tags: firebase.firestore.FieldValue.arrayUnion(clean), ...stamp(user) },
    { merge: true },
  );
}
